import React, {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import axiosApiInstance from '../../../api/axiosClient';
import axios from 'axios';
import {PageContextProvider} from './DDEXMainPage';
import StepsFormFirstPage, {generatedUPCAtom, getImageUrl} from './StepFormFirstPage';
import StepsFormSecondPage from './StepsFormSecondPage';
import StepsFormThirdPage from './StepsFormThirdPage';
import FileUploadStep, {uploadedFilesAtom} from './FileUploadStep';
import {Button, Form, Input, message, Modal, Spin, Steps} from 'antd';
import {fetchReleaseFiles} from './fileUtils';
import {useAtom, useAtomValue} from 'jotai';
import './ReleaseEntryStepForm.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSave} from '@fortawesome/free-solid-svg-icons';
import {
    artTracksFormAtom,
    currentStepAtom,
    previewImageAtom,
    releaseInfoFormAtom,
    submitFormAtom,
    uploadProgressAtom,
    useArtTracksForm,
    useReleaseInfoForm,
    useSubmitForm
} from './release_form_atoms';
import {confirmationModalAtom, isReleaseInfoLoadingAtom, warningsAtom} from './ddex_atoms';

// TreeSelect keeps checked countries as an array. The release record stores one string.
function formatTerritoriesForForm(value: string | string[] | undefined) {
    if (Array.isArray(value)) return value;
    if (!value || value === 'Worldwide') return value;
    return value.split(/[,;]+/).filter(Boolean);
}

const CreateReleasePage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [existingFilesLoading, setExistingFilesLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [modalVisible, setModalVisible] = React.useState(false);
    const [releaseTitle, setReleaseTitle] = React.useState('');
    const [generatedUPC, setGeneratedUPC] = useAtom(generatedUPCAtom);
    const [uploadedFiles, setUploadedFiles] = useAtom(uploadedFilesAtom);
    const [uploadProgress, setUploadProgress] = useAtom(uploadProgressAtom);
    const [currentTab, setCurrentTab] = useAtom(currentStepAtom);
    const [warnings, setWarnings] = useAtom(warningsAtom);
    const [isReleaseInfoLoading, setIsReleaseInfoLoading] = useAtom(isReleaseInfoLoadingAtom);
    const [previewImage, setPreviewImage] = useAtom(previewImageAtom);
    const [confirmationModal, setConfirmationModal] = useAtom(confirmationModalAtom);

    // Form instances from jotai atoms
    const [releaseInfoForm] = Form.useForm();
    const [, setReleaseInfoForm] = useAtom(useReleaseInfoForm);
    const [artTracksForm] = Form.useForm();
    const [, setArtTracksForm] = useAtom(useArtTracksForm);
    const [submitForm] = Form.useForm();
    const [, setSubmitForm] = useAtom(useSubmitForm);
    const [releaseInfoData, setReleaseInfoData] = useAtom(releaseInfoFormAtom);
    const [artTracksData, setArtTracksData] = useAtom(artTracksFormAtom);
    const [submitData, setSubmitData] = useAtom(submitFormAtom);

    // For backward compatibility with PageContextProvider
    const releasesActionRef = React.useRef<any>(null);
    const listRef = React.useRef<any>(null);
    const [artists, setArtists] = React.useState<any>({});

    useEffect(() => {
        setReleaseInfoForm(releaseInfoForm);
        setArtTracksForm(artTracksForm);
        setSubmitForm(submitForm);
    }, [releaseInfoForm, artTracksForm, submitForm]);

    // If no ID is provided in the URL, show the modal to enter release title
    useEffect(() => {
        if (!id) {
            setModalVisible(true);
            setLoading(false);
        } else {
            setLoading(false);
        }
    }, [id]);

    // Function to handle the modal submission
    const handleModalSubmit = () => {
        if (!releaseTitle.trim()) {
            message.error('Please enter a release title');
            return;
        }

        setLoading(true);

        // Create a release entry with the title and generate UUID in one step
        const values = {
            name: releaseTitle,
            // Add any other required fields with default values
            artists: [],
            featuring_artists: []
        };

        axiosApiInstance.post('/api/products/releases/save-release-info', values)
            .then(response => {
                if (response.status === 200 && response.data?.release_info?.id) {
                    // Set the release title and ID in the form data
                    setReleaseInfoData({
                        ...releaseInfoData,
                        name: releaseTitle,
                        id: response.data.release_info.id
                    });

                    // Navigate to the create page with the new UUID
                    navigate(`/music/create/${response.data.release_info.id}`, {replace: true});
                    setModalVisible(false);
                } else {
                    throw new Error('Failed to create release entry');
                }
            })
            .catch(err => {
                console.error('Error creating release entry:', err);
                setError('Failed to create release entry');
                setLoading(false);
            });
    };

    // Load any previously uploaded files for this release
    useEffect(() => {
        const loadExistingFiles = async () => {
            if (!id) return;

            const files = await fetchReleaseFiles(id, setUploadedFiles, setExistingFilesLoading);
            console.log(`Loaded ${files.length} files for release ${id}`);
        };

        // Always load existing files when the component mounts, regardless of the current step
        if (id) {
            console.log(`Loading existing files for release ${id}`);
            loadExistingFiles();
        }
    }, [id]);

    // Effect to initialize form values
    // useEffect(() => {
    //     releaseInfoForm.setFieldsValue(releaseInfoData);
    //     artTracksForm.setFieldsValue(artTracksData);
    //     submitForm.setFieldsValue(submitData);
    //
    //     // Validate all fields on initialization
    //     if (Object.keys(releaseInfoData).length > 0) {
    //         // Use validateFields to validate the form but don't reject the promise
    //         releaseInfoForm.validateFields().catch(() => {
    //             // Validation errors are expected and handled by the form
    //             console.log('Initial form validation complete with warnings');
    //         });
    //     }
    //
    //     if (Object.keys(artTracksData).length > 0) {
    //         artTracksForm.validateFields().catch(() => {
    //             console.log('Initial art tracks validation complete with warnings');
    //         });
    //     }
    // }, []);


    // Initialize currentTab if not set or if it's the default value
    useEffect(() => {
        if (!currentTab) {
            setCurrentTab('upload-files');
        }
    }, [currentTab]);

    const handleFirstPageFlip = async (
        main_artists: API.MusicDistribution.Artist[],
        featuring_artists: API.MusicDistribution.Artist[]
    ) => {
        console.log('handleFirstPageFlip: ', main_artists, featuring_artists);
        const tracks = artTracksForm.getFieldValue('art_tracks');
        console.log('tracks: ', tracks);
        artTracksForm.setFieldValue(
            'art_tracks',
            tracks?.map((track: API.DDEX.Track) => ({...track, main_artists, featuring_artists}))
        );
        // Update the atom with the new data
        setArtTracksData(artTracksForm.getFieldsValue());
        console.log('end handleFirstPageFlip');
    };

    const handleReleaseInfoFinish = async (values: any) => {
        setReleaseInfoData(values);
        await handleFirstPageFlip(values.artists, values.featuring_artists);
        setCurrentTab('art-tracks');
    };

    const handleArtTracksFinish = async (values: any) => {
        setArtTracksData(values);
        setCurrentTab('submit');
    };

    // Function to save only release level information
    const handleSaveReleaseInfo = async () => {
        try {
            // Validate the form first
            await releaseInfoForm.validateFields();

            setIsSaving(true);
            setIsReleaseInfoLoading(true);
            // Get the current form values
            let values = releaseInfoForm.getFieldsValue();

            // Try to get the ID from the URL path if it's not in the form
            if (!values.id) {
                // Format: /music/create/101373928068349956
                const pathParts = window.location.pathname.split('/');
                const idFromUrl = pathParts[pathParts.length - 1];

                if (idFromUrl && /^\d+$/.test(idFromUrl)) {
                    values.id = idFromUrl;
                    releaseInfoForm.setFieldValue('id', idFromUrl);
                    // Update the atom with the new data
                    setReleaseInfoData({...releaseInfoData, id: idFromUrl});
                    console.log('Using ID from URL path:', idFromUrl);
                } else {
                    console.error('No ID found in URL path. Required for saving.');
                    message.error('Missing release ID. Cannot save without an ID.');
                    setIsSaving(false);
                    return;
                }
            }

            // TreeSelect stores checked territories as an array. The API field is a string.
            const territories = Array.isArray(values.territories)
                ? values.territories.filter(Boolean).join(';') || 'Worldwide'
                : values.territories || 'Worldwide';

            // Apply the same transformations as in the onFinish method
            values = {
                ...values,
                territories,
                artists: values.artists?.map((key_name) => ({key_name})),
                featuring_artists: values.featuring_artists?.map((key_name) => ({key_name}))
            };
            console.log("Data being sent to SAVE API:", values.artists);
            if (values.id) {
                console.log('Saving release with existing ID:', values.id);
            } else {
                console.log('Saving release without ID, backend will generate one');
            }

            // Make API call to save release level info
            const response = await axiosApiInstance.post('/api/products/releases/save-release-info', values);
            await fetchReleaseData(id)

        } catch (error) {
            console.error('Error saving release info:', error);

            if (error.response?.data?.error) {
                message.error(`Failed to save: ${error.response.data.error}`);
            } else if (error.message) {
                message.error(`Failed to save: ${error.message}`);
            } else {
                message.error('Failed to save release information');
            }
        } finally {
            setIsSaving(false);
            setIsReleaseInfoLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            // Fetch preview data to show in confirmation modal
            try {
                const response = await axiosApiInstance.get(`/api/products/releases/${id}/previews`);
                if (response.data) {
                    // Count how many UPCs and ISRCs will be created
                    const upcCount = response.data.preview_upc && !response.data.release.upc_ean ? 1 : 0;
                    const isrcCount = Object.keys(response.data.preview_isrcs || {}).length;

                    // Set confirmation modal data
                    setConfirmationModal({
                        visible: true,
                        upcCount,
                        isrcCount,
                        previewUPC: response.data.preview_upc || '',
                        previewISRCs: response.data.preview_isrcs || {},
                        validationWarnings: warnings
                    });

                    return true;
                }
            } catch (error) {
                console.error('Error fetching preview data:', error);
                message.error('Failed to fetch preview data');
                return false;
            }

            return false;
        } catch (error) {
            console.error('Validation failed:', error);
            return false;
        }
    };

    // Function to handle the actual submission after confirmation
    const handleConfirmedSubmit = async () => {
    try {
        setConfirmationModal({...confirmationModal, visible: false});
        
        if (!id) {
            message.error("Cannot submit without a release ID.");
            return false;
        }
        
        console.log(`Finalizing artist order for release ${id}...`);
        await axiosApiInstance.post(`/api/products/releases/${id}/finalize-order`);
        console.log("Artist order finalized.");

        console.log(`Assigning identifiers for release ${id}...`);
        await axiosApiInstance.post(`/api/products/releases/assign-identifiers`, { key: id });
        console.log("Identifiers assigned.");

        releasesActionRef?.current?.reload();
        setUploadProgress(0);
        navigate('/music');
        return true;
        
    } catch (error) {
        console.error('Submission failed:', error);
        let errorMsg = 'Failed to submit release.';
        if(error.response?.data?.error) {
            errorMsg = error.response.data.error;
        }
        message.error(errorMsg);
        return false;
    }
};

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    // Map currentTab to step number for Steps component
    const getStepNumber = () => {
        switch (currentTab) {
            case 'upload-files':
                return 0;
            case 'release-info':
                return 1;
            case 'art-tracks':
                return 2;
            case 'submit':
                return 3;
            default:
                return 0;
        }
    };

    // Function to fetch release data by ID
    const fetchReleaseData = async (releaseId: string) => {
        try {
            console.log(`Fetching release data for ID: ${releaseId}`);
            setIsReleaseInfoLoading(true);
            // Clear any existing warnings using the atom
            setWarnings([]);
            const response = await axiosApiInstance.get(`/api/products/releases/${releaseId}`,
                {params: {without_tracks: true}});

            if (response.status === 200 && response.data) {
                console.log('Successfully fetched release data:', response.data);
                const releaseData = response.data as API.MusicDistribution.MusicRelease

                // Format the data for the form
                const formData = {
                    id: releaseData.id,
                    name: releaseData.name,
                    subtitle: releaseData.subtitle,
                    // Keys must match the form field names in StepFormFirstPage,
                    // otherwise saved values silently fail to reload into the form.
                    artist_display: releaseData.artist_display,
                    upc_ean: releaseData.upc_ean,
                    label_id: releaseData.label_id,
                    asset_label_id: releaseData.asset_label_id,
                    p_line: releaseData.p_line ?? releaseData.label?.p_line ?? '',
                    c_line: releaseData.c_line ?? releaseData.label?.c_line ?? '',
                    digital_release_date: releaseData.digital_release_date ? new Date(releaseData.digital_release_date) : undefined,
                    production_year: releaseData.production_year ? new Date(releaseData.production_year) : undefined,
                    original_release_date: releaseData.original_release_date ? new Date(releaseData.original_release_date) : undefined,
                    genre: releaseData.genre,
                    sub_genre: releaseData.sub_genre,
                    type: releaseData.type,
                    price_tier: releaseData.price_tier,
                    territories: formatTerritoriesForForm(releaseData.territories),
                    album_cover_filename: releaseData.album_cover_filename,
                    // Format the artists arrays to match form expectations
                    artists: releaseData.artists?.map(artist => artist.key_name) || [],
                    featuring_artists: releaseData.featuring_artists?.map(artist => artist.key_name) || [],
                };

                // Set form values
                releaseInfoForm.setFieldsValue(formData);
                // Update the atom with the new data
                setReleaseInfoData({...releaseInfoData, ...formData});

                // If there's a cover art filename, try to preview it
                if (formData.album_cover_filename) {
                    const imageUrl = await getImageUrl(formData.album_cover_filename, releaseInfoForm.getFieldValue('id'));
                    if (imageUrl) {
                        setPreviewImage(imageUrl);
                    }
                }
                console.log({data: response.data});
                // message.success('Release data loaded successfully');

                // Check if there are any warnings in the response
                if (response.data.validation_warnings && Array.isArray(response.data.validation_warnings)) {
                    setWarnings(response.data.validation_warnings);
                }

                // Validate form fields after fetching data
                // This will trigger all validation rules and show warnings if necessary
                // setTimeout(() => {
                //     releaseInfoForm.validateFields().catch(() => {
                //         console.log('Form validation after fetching complete with warnings');
                //     });
                // }, 300);
                releaseInfoForm.validateFields().catch(() => {
                    console.log('Form validation after fetching complete with warnings');
                });
            }
        } catch (error) {
            console.error('Error fetching release data:', error);
            message.error('Failed to load release data');
        } finally {
            setIsReleaseInfoLoading(false);
        }
    };

    return (
        <PageContextProvider
            value={{
                releasesActionRef,
                listRef,
                artists,
                setArtists
            }}
        >
            <div className="release-entry-container" style={{width: '100%', maxWidth: 1600, margin: '0 auto'}}>
                <div className="release-entry-header"
                     style={{display: 'flex', justifyContent: 'space-between', marginBottom: 20}}>
                    <h2>Create New Release</h2>
                    <div>
                        {/*<Button*/}
                        {/*    key="back"*/}
                        {/*    onClick={() => {*/}
                        {/*        if (generatedUPC != '') {*/}
                        {/*            axiosApiInstance.put(`/api/products/unbind_upc/${generatedUPC}`);*/}
                        {/*            setGeneratedUPC('');*/}
                        {/*        }*/}
                        {/*        navigate('/music');*/}
                        {/*    }}*/}
                        {/*    style={{marginRight: 8}}*/}
                        {/*>*/}
                        {/*    Cancel*/}
                        {/*</Button>*/}
                        <Button
                            key="submit"
                            type="primary"
                            onClick={handleSubmit}
                            disabled={currentTab !== 'submit'}
                        >
                            Submit
                        </Button>
                        {currentTab === 'upload-files' && (
                            <Button
                                key="next-upload-files"
                                type="primary"
                                onClick={() => {
                                    setIsReleaseInfoLoading(true);
                                    setCurrentTab('release-info');
                                    // Reset loading state after a short delay
                                    setTimeout(() => setIsReleaseInfoLoading(false), 300);
                                }}
                                style={{marginLeft: 8}}
                            >
                                Next
                            </Button>
                        )}
                        {currentTab === 'release-info' && (
                            <>
                                <Button
                                    key="save-release-info"
                                    onClick={handleSaveReleaseInfo}
                                    loading={isSaving}
                                    icon={<FontAwesomeIcon style={{marginRight: 8}} icon={faSave}/>}
                                    style={{marginLeft: 8}}
                                >
                                    Save
                                </Button>
                                <Button
                                    key="next-release-info"
                                    type="primary"
                                    onClick={async () => {
                                        // await handleSaveReleaseInfo();
                                        releaseInfoForm.submit();
                                    }}
                                    style={{marginLeft: 8}}
                                >
                                    Next
                                </Button>
                            </>
                        )}
                        {currentTab === 'art-tracks' && (
                            <Button
                                key="next-art-tracks"
                                type="primary"
                                onClick={() => artTracksForm.submit()}
                                style={{marginLeft: 8}}
                            >
                                Next
                            </Button>
                        )}
                    </div>
                </div>
                <div style={{marginBottom: 20}}>
                    <Steps
                        current={getStepNumber()}
                        onChange={(current) => {
                            // Map step number back to tab key
                            const tabKeys = ['upload-files', 'release-info', 'art-tracks', 'submit'];
                            const newTab = tabKeys[current];

                            if (currentTab === 'release-info' && newTab !== 'release-info') {
                                setReleaseInfoData((prev) => ({
                                    ...prev,
                                    ...releaseInfoForm.getFieldsValue(),
                                }));
                            }

                            // Set loading state when changing to release-info tab
                            if (newTab === 'release-info') {
                                setIsReleaseInfoLoading(true);
                                // Reset loading state after a short delay to allow the tab to render
                                setTimeout(() => {
                                    setIsReleaseInfoLoading(false);

                                    // Validate form fields when the tab is shown
                                    releaseInfoForm.validateFields().catch(() => {
                                        console.log('Form validation on step change complete with warnings');
                                    });
                                }, 300);
                            } else if (newTab === 'art-tracks') {
                                // Validate art tracks form when switching to it
                                setTimeout(() => {
                                    artTracksForm.validateFields().catch(() => {
                                        console.log('Art tracks validation on step change complete with warnings');
                                    });
                                }, 300);
                            }

                            setCurrentTab(newTab);
                        }}
                        type="navigation"
                        items={[
                            {
                                title: 'Upload Files',
                                // key: 'upload-files'
                            },
                            {
                                title: 'Release Information',
                                // key: 'release-info'
                            },
                            {
                                title: 'Tracks',
                                // key: 'art-tracks'
                            },
                            {
                                title: 'Verify',
                                // key: 'submit'
                            }
                        ]}
                    />
                </div>
                <div style={{marginTop: 24}}>
                    {currentTab === 'upload-files' && (
                        <FileUploadStep edit={false} isLoadingExistingFiles={existingFilesLoading}/>
                    )}

                    {currentTab === 'release-info' && (
                        <Form
                            form={releaseInfoForm}
                            layout='horizontal'
                            labelAlign='left'
                            preserve
                            onFinish={handleReleaseInfoFinish}
                        >
                            <StepsFormFirstPage
                                initialValues={{
                                    upc_ean: 12345600000
                                }}
                                edit={false}
                                fetchReleaseData={fetchReleaseData}
                            />
                        </Form>
                    )}

                    {currentTab === 'art-tracks' && (
                        <Form
                            form={artTracksForm}
                            onFinish={handleArtTracksFinish}
                        >
                            <StepsFormSecondPage edit={false}/>
                        </Form>
                    )}

                    {currentTab === 'submit' && (
                        <Form
                            form={submitForm}
                        >
                            <StepsFormThirdPage/>
                        </Form>
                    )}
                </div>
            </div>

            {/* Modal for entering release title */}
            <Modal
                title="Enter Release Title"
                open={modalVisible}
                onOk={handleModalSubmit}
                onCancel={() => navigate('/music')}
                okText="Create Release"
                cancelText="Cancel"
                closable={false}
                maskClosable={false}
                keyboard={false}
            >
                <p>Please enter a title for your new release:</p>
                <Input
                    placeholder="Release Title"
                    value={releaseTitle}
                    onChange={(e) => setReleaseTitle(e.target.value)}
                    onPressEnter={handleModalSubmit}
                    autoFocus
                />
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                title="Confirm Submission"
                open={confirmationModal.visible}
                onOk={handleConfirmedSubmit}
                onCancel={() => setConfirmationModal({...confirmationModal, visible: false})}
                okText="Submit"
                cancelText="Cancel"
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <h3>The following will be created upon submission:</h3>
                    <ul>
                        {confirmationModal.upcCount > 0 && (
                            <li>
                                <strong>{confirmationModal.upcCount}</strong> UPC will be created for the release
                            </li>
                        )}
                        {confirmationModal.isrcCount > 0 && (
                            <li>
                                <strong>{confirmationModal.isrcCount}</strong> ISRCs will be created for tracks
                            </li>
                        )}
                    </ul>
                </div>

                {confirmationModal.validationWarnings.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <h3>Warning: There are still validation issues:</h3>
                        <ul>
                            {confirmationModal.validationWarnings.map((warning, index) => (
                                <li key={index}>
                                    {warning.group === 'track' ? 'Track' : 'Release'}: {warning.message} ({warning.field})
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f0f2f5', borderRadius: 4 }}>
                    <p><strong>Note:</strong> After submission, you can upload this release to DSPs via the Releases page.</p>
                </div>
            </Modal>
        </PageContextProvider>
    );
};

export default CreateReleasePage;
